/**
 * this plugin adds the json export/import capabilities to RxDB
 */
import {
    hash
} from '../util';
import {
    createRxQuery,
    _getDefaultQuery
} from '../rx-query';
import {
    newRxError
} from '../rx-error';
import type {
    RxDatabase,
    RxCollection,
    RxPlugin,
    RxDocumentData,
    BulkWriteRow
} from '../types';
import { _handleToStorageInstance } from '../rx-collection-helper';

function dumpRxDatabase(
    this: RxDatabase,
    decrypted = false,
    collections?: string[]
): Promise<any> {
    const json: any = {
        name: this.name,
        instanceToken: this.token,
        encrypted: false,
        passwordHash: null,
        collections: []
    };

    if (this.password) {
        json.passwordHash = hash(this.password);
        if (decrypted) json.encrypted = false;
        else json.encrypted = true;
    }

    const useCollections = Object.keys(this.collections)
        .filter(colName => !collections || collections.includes(colName))
        .filter(colName => colName.charAt(0) !== '_')
        .map(colName => this.collections[colName]);

    return Promise.all(
        useCollections
            .map(col => col.exportJSON(decrypted))
    ).then(cols => {
        json.collections = cols;
        return json;
    });
}

const importDumpRxDatabase = function (
    this: RxDatabase,
    dump: any
) {
    /**
     * collections must be created before the import
     * because we do not know about the other collection-settings here
     */
    const missingCollections = dump.collections
        .filter((col: any) => !this.collections[col.name])
        .map((col: any) => col.name);
    if (missingCollections.length > 0) {
        throw newRxError('JD1', {
            missingCollections
        });
    }

    return Promise.all(
        dump.collections
            .map((colDump: any) => this.collections[colDump.name].importJSON(colDump))
    );
};

const dumpRxCollection = function (
    this: RxCollection,
    decrypted = false
) {
    const encrypted = !decrypted;

    const json: any = {
        name: this.name,
        schemaHash: this.schema.hash,
        encrypted: false,
        passwordHash: null,
        docs: []
    };

    if (this.database.password && encrypted) {
        json.passwordHash = hash(this.database.password);
        json.encrypted = true;
    }

    const query = createRxQuery(
        'find',
        _getDefaultQuery(),
        this
    );
    return this._queryStorageInstance(query, undefined, encrypted)
        .then((docs: any) => {
            json.docs = docs.map((docData: any) => {
                delete docData._rev;
                delete docData._attachments;
                return docData;
            });
            return json;
        });
};

function importDumpRxCollection<RxDocType>(
    this: RxCollection<RxDocType>,
    exportedJSON: any
): Promise<any> {
    // check schemaHash
    if (exportedJSON.schemaHash !== this.schema.hash) {
        throw newRxError('JD2', {
            schemaHash: exportedJSON.schemaHash,
            own: this.schema.hash
        });
    }

    // check if passwordHash matches own
    if (
        exportedJSON.encrypted &&
        exportedJSON.passwordHash !== hash(this.database.password)
    ) {
        throw newRxError('JD3', {
            passwordHash: exportedJSON.passwordHash,
            own: hash(this.database.password)
        });
    }

    const docs: RxDocumentData<RxDocType>[] = exportedJSON.docs
        // decrypt
        .map((doc: any) => this._crypter.decrypt(doc))
        // validate schema
        .map((doc: any) => this.schema.validate(doc));

    return this.database.lockedRun(
        // write to disc
        () => {
            const writeMe: BulkWriteRow<RxDocType>[] = docs.map(doc => ({
                document: _handleToStorageInstance(this, doc)
            }));
            return this.storageInstance.bulkWrite(writeMe);
        }
    );
}

export const rxdb = true;
export const prototypes = {
    RxDatabase: (proto: any) => {
        proto.exportJSON = dumpRxDatabase;
        proto.importJSON = importDumpRxDatabase;
    },
    RxCollection: (proto: any) => {
        proto.exportJSON = dumpRxCollection;
        proto.importJSON = importDumpRxCollection;
    }
};

export const overwritable = {};

export const RxDBJsonDumpPlugin: RxPlugin = {
    name: 'json-dump',
    rxdb,
    prototypes,
    overwritable
};
